import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import Typography from '@latitude/typography';
import { Link } from '@latitude/link';
import StandaloneTextBody from './StandaloneTextBody';
import styled from 'styled-components';
import Section from '@latitude/section-rebrand';
import Button from '@latitude/button-rebrand';

const Wrapper = styled.div`
  background-color: ${({ background }) => background};
`;

const StyledSection = styled(Section)`
  padding-top: 32px;
  padding-bottom: 32px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: ${({ align }) => align };

  > * {
    margin: 0;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${({ textColor }) => textColor};
    margin: 0;
  }

  p,
  li {
    color: ${({ textColor }) => textColor};
    margin: 0;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: ${({ align }) => {
    switch (align) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      default:
        return 'center';
    }
  }};
`;

const StandaloneText = ({
  text,
  align,
  background,
  textColor,
  button,
  buttonTheme,
  buttonsAlign
}) => {
  const options = {
    renderNode: {
      [BLOCKS.HEADING_1]: (_node, children) => (
        <Typography.H3 semibold>{children}</Typography.H3>
      ),

      [BLOCKS.HEADING_2]: (_node, children) => (
        <Typography.H4 semibold>{children}</Typography.H4>
      ),

      [BLOCKS.HEADING_3]: (_node, children) => (
        <Typography.SH1 semibold>{children}</Typography.SH1>
      ),

      [BLOCKS.HEADING_4]: (_node, children) => (
        <Typography.SH2 semibold>{children}</Typography.SH2>
      ),

      [BLOCKS.HEADING_5]: (_node, children) => (
        <Typography.SH3 semibold>{children}</Typography.SH3>
      ),

      [BLOCKS.HEADING_6]: (_node, children) => (
        <Typography.SH4 semibold>{children}</Typography.SH4>
      ),

      [BLOCKS.PARAGRAPH]: (_node, children) => (
        <Typography.Body>{children}</Typography.Body>
      ),

      [INLINES.HYPERLINK]: (node, children) => (
        <Typography.LinkRegular href={node?.data?.uri}>
          {children}
        </Typography.LinkRegular>
      ),

      [INLINES.EMBEDDED_ENTRY]: node => {
        if (node?.data?.target?.contentType?.sys?.id === 'standaloneTextBody') {
          return <StandaloneTextBody {...node.data.target} />;
        }

        return null;
      }
    }
  };

  const alignment =
    align?.toUpperCase() === 'LEFT'
      ? 'left'
      : align?.toUpperCase() === 'RIGHT'
      ? 'right'
      : 'center';

  const getButtonType = theme => {
    if (theme === 'Outline') return 'secondary-white';
    else if (theme === 'Blue') return 'secondary-blue';
    return 'primary';
  };

  const buttonAlignment =
    buttonsAlign?.toUpperCase() === 'LEFT' ? 'left' : 'center';

  return (
    <Wrapper background={background}>
      <StyledSection>
        <ContentWrapper align={alignment} textColor={textColor}>
          {documentToReactComponents(text, options)}
          {button?.url && <ButtonWrapper align={buttonAlignment}>
            <Button
              variant={getButtonType(buttonTheme)}
              size="lg"
              ctaText={button?.text}
              ctaUrl={button?.url}
              trackEventData={{
                category: 'text-link',
                location: 'standalone-text',
                label: button?.text
              }}
              trackId="standalone-text"
            />
          </ButtonWrapper>}
        </ContentWrapper>
      </StyledSection>
    </Wrapper>
  );
};

export default StandaloneText;
